import { useMemo, useState } from 'react';
import { useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { useCan } from '@/hooks/useCan';
import type { MultiSelectOption } from '@/components/common/MultiSelect';
import useDivisions from '@/api/organization/useDivisions';
import useEligibleRebateSuppliers from '@/api/rebates/useEligibleRebateSuppliers';
import { usePurchaseTrend } from '@/api/rpc/hooks/rebates/usePurchaseTrend';
import {
  useDeletePurchaseUpload,
  usePurchaseUploads,
} from '@/api/rpc/hooks/rebates/usePurchaseUpload';
import { lastUploadThroughMonth } from './purchaseTrendChart';
import {
  assemblePurchaseTrendChartModel,
  buildDivisionOptions,
  buildPurchaseTrendTitle,
  buildSupplierOptions,
  buildUploadOptions,
  lastUploadCardModel,
  purchaseTrendInitialLoading,
  purchaseTrendIsSettled,
  purchaseUploadsStatus,
  purchaseTrendPageYear,
  purchaseTrendSupplierInput,
  resolveSelectedUploadId,
  selectedUploadRecord,
  trendFilterIds,
  uploadInViewRecord,
  lockedLabelsFromNamed,
  optionsFromLockedIds,
  purchaseTrendEffectiveUploadId,
  type LastUploadRecord,
  type PurchaseTrendLockedFilters,
} from './purchaseTrendPage';

function useHasCompletedInitialLoad(isSettled: boolean) {
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);
  if (isSettled && !hasCompletedInitialLoad) {
    setHasCompletedInitialLoad(true);
  }
  return hasCompletedInitialLoad;
}

function usePurchaseMonthLabels() {
  const gt = useGT();
  const short = useMemo(
    () => [
      gt('Jan', { context: 'short calendar month name' }),
      gt('Feb', { context: 'short calendar month name' }),
      gt('Mar', { context: 'short calendar month name' }),
      gt('Apr', { context: 'short calendar month name' }),
      gt('May', { context: 'short calendar month name' }),
      gt('Jun', { context: 'short calendar month name' }),
      gt('Jul', { context: 'short calendar month name' }),
      gt('Aug', { context: 'short calendar month name' }),
      gt('Sep', { context: 'short calendar month name' }),
      gt('Oct', { context: 'short calendar month name' }),
      gt('Nov', { context: 'short calendar month name' }),
      gt('Dec', { context: 'short calendar month name' }),
    ],
    [gt],
  );
  const full = useMemo(
    () => [
      gt('January', { context: 'full calendar month name' }),
      gt('February', { context: 'full calendar month name' }),
      gt('March', { context: 'full calendar month name' }),
      gt('April', { context: 'full calendar month name' }),
      gt('May', { context: 'full calendar month name' }),
      gt('June', { context: 'full calendar month name' }),
      gt('July', { context: 'full calendar month name' }),
      gt('August', { context: 'full calendar month name' }),
      gt('September', { context: 'full calendar month name' }),
      gt('October', { context: 'full calendar month name' }),
      gt('November', { context: 'full calendar month name' }),
      gt('December', { context: 'full calendar month name' }),
    ],
    [gt],
  );
  return { short, full };
}

function usePurchaseTrendFilters(
  lockedFilters: PurchaseTrendLockedFilters | undefined,
  optionsEnabled: boolean,
) {
  const gt = useGT();
  const unlabeled = gt('Shared filter', {
    context: 'label when a shared purchase-trend filter name is missing',
  });
  const isShared = lockedFilters !== undefined;
  const [selectedSuppliers, setSelectedSuppliers] = useState<
    MultiSelectOption[]
  >(() => optionsFromLockedIds(lockedFilters?.supplierIds));
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >(() => optionsFromLockedIds(lockedFilters?.divisionIds));
  const { data: suppliersData, isLoading: suppliersLoading } =
    useEligibleRebateSuppliers(optionsEnabled, {
      year: lockedFilters?.year,
      uploadId: lockedFilters?.uploadId,
    });
  const { data: divisionsData, isLoading: divisionsLoading } = useDivisions(
    optionsEnabled && !isShared,
  );
  const supplierOptions = useMemo(
    () => buildSupplierOptions(suppliersData),
    [suppliersData],
  );
  const divisionOptions = useMemo(
    () => buildDivisionOptions(divisionsData?.divisions),
    [divisionsData?.divisions],
  );
  const effectiveSuppliers = lockedFilters
    ? optionsFromLockedIds(
        lockedFilters.supplierIds,
        lockedLabelsFromNamed(lockedFilters.supplierIds, supplierOptions),
        unlabeled,
      )
    : selectedSuppliers;
  const effectiveDivisions = lockedFilters
    ? optionsFromLockedIds(lockedFilters.divisionIds, {}, unlabeled)
    : selectedDivisions;
  return {
    isShared,
    selectedSuppliers: effectiveSuppliers,
    setSelectedSuppliers,
    selectedDivisions: effectiveDivisions,
    setSelectedDivisions,
    supplierOptions,
    divisionOptions,
    suppliersLoading,
    divisionsLoading,
  };
}

function usePurchaseTrendChartModel(
  selectedYear: number,
  resolvedUploadId: string | null,
  selectedSuppliers: MultiSelectOption[],
  selectedDivisions: MultiSelectOption[],
  monthLabels: string[],
  fullMonthNames: string[],
  enabled: boolean,
) {
  const supplierInput = purchaseTrendSupplierInput(selectedSuppliers);
  const selectedSupplierId = supplierInput.supplierId ?? null;
  const { data, isLoading, error } = usePurchaseTrend(
    {
      year: selectedYear,
      uploadId: resolvedUploadId ?? undefined,
      ...supplierInput,
      divisionIds: trendFilterIds(selectedDivisions),
      lineType: 'reported',
    },
    enabled,
  );
  const model = useMemo(
    () =>
      assemblePurchaseTrendChartModel({
        data,
        isLoading,
        error,
        selectedSupplierId,
        monthLabels,
        fullMonthNames,
      }),
    [data, error, fullMonthNames, isLoading, monthLabels, selectedSupplierId],
  );
  return {
    ...model,
    trendLoading: isLoading,
    trendError: error,
    hasTrendData: data !== undefined,
  };
}

function usePurchaseTrendYearAndUploads(
  lockedFilters: PurchaseTrendLockedFilters | undefined,
  selectedUploadId: string | null,
  monthLabels: string[],
  labels: { latest: string; replacedOn: string; dataMissing: string },
) {
  const calendarYear = new Date().getFullYear();
  const lockedYear = lockedFilters?.year;
  const primaryYear = lockedYear ?? calendarYear;
  const priorYear = primaryYear - 1;
  const primary = usePurchaseUploads(primaryYear);
  const prior = usePurchaseUploads(priorYear, lockedYear === undefined);
  const primaryUploads = primary.data ?? [];
  const priorUploads = prior.data ?? [];
  const selectedYear = purchaseTrendPageYear({
    lockedYear,
    currentYear: primaryYear,
    currentUploads: primaryUploads,
    priorUploads,
  });
  const uploads = selectedYear === priorYear ? priorUploads : primaryUploads;
  const uploadOptions = useMemo(
    () => buildUploadOptions(uploads, monthLabels, labels),
    [labels, monthLabels, uploads],
  );
  return {
    selectedYear,
    uploads,
    ...purchaseUploadsStatus({ primary, prior, lockedYear }),
    uploadOptions,
    resolvedUploadId: resolveSelectedUploadId(uploads, selectedUploadId),
  };
}

function usePurchaseTrendFileCheck(
  lockedFilters: PurchaseTrendLockedFilters | undefined,
  monthLabels: string[],
  labels: { latest: string; replacedOn: string; dataMissing: string },
) {
  const [pickedUploadId, setSelectedUploadId] = useState<string | null>(
    lockedFilters?.uploadId ?? null,
  );
  const selectedUploadId = purchaseTrendEffectiveUploadId(
    lockedFilters,
    pickedUploadId,
  );
  const uploadList = usePurchaseTrendYearAndUploads(
    lockedFilters,
    selectedUploadId,
    monthLabels,
    labels,
  );
  const hasNoFile =
    uploadList.uploadsLoaded && uploadList.resolvedUploadId === null;
  return {
    ...uploadList,
    selectedUploadId,
    setSelectedUploadId,
    hasNoFile,
    reportEnabled: !uploadList.uploadsLoading && !hasNoFile,
  };
}

function usePurchaseTrendUpload(input: {
  selectedYear: number;
  uploads: Array<{
    id: string;
    fileName: string;
    uploadedAt: Date;
    throughMonth: number;
  }>;
  resolvedUploadId: string | null;
  onSelectLatest: () => void;
  monthLabels: string[];
  chartSourceMonths: ReturnType<
    typeof usePurchaseTrendChartModel
  >['chartSourceMonths'];
  uploadsInView: ReturnType<typeof usePurchaseTrendChartModel>['uploadsInView'];
}) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [lastUpload, setLastUpload] = useState<LastUploadRecord | null>(null);
  const [canUpload] = useCan(Action.Execute, Subject.RebateDataUpload);
  const selectedFromList = selectedUploadRecord(
    input.uploads,
    input.resolvedUploadId,
    input.selectedYear,
  );
  const uploadInView = uploadInViewRecord(
    input.uploadsInView,
    input.resolvedUploadId,
    input.selectedYear,
  );
  const lastUploadCard = useMemo(
    () =>
      lastUploadCardModel({
        lastUpload,
        selectedUpload: selectedFromList,
        uploadInView,
        selectedYear: input.selectedYear,
        monthLabels: input.monthLabels,
        inferredThroughMonth: lastUploadThroughMonth(
          input.chartSourceMonths ?? [],
        ),
      }),
    [
      input.chartSourceMonths,
      input.monthLabels,
      input.selectedYear,
      lastUpload,
      selectedFromList,
      uploadInView,
    ],
  );
  function handleImported(record: LastUploadRecord) {
    setLastUpload(record);
    input.onSelectLatest();
  }
  return {
    uploadOpen,
    setUploadOpen,
    handleImported,
    canUpload,
    lastUploadCard,
  };
}

export function usePurchaseTrendPage(
  lockedFilters?: PurchaseTrendLockedFilters,
) {
  const monthLabels = usePurchaseMonthLabels();
  const gt = useGT();
  const latestLabel = gt('latest', {
    context: 'suffix marking the newest purchases upload in the picker',
  });
  const replacedOnLabel = gt('replaced on', {
    context: 'suffix for a purchases upload that a later file replaced',
  });
  const dataMissingLabel = gt("File data wasn't kept, re-upload to view", {
    context: 'suffix for a purchases upload whose stored rows were not kept',
  });
  const unlabeled = gt('Shared filter', {
    context: 'label when a shared purchase-trend filter name is missing',
  });
  const uploadOptionLabels = useMemo(
    () => ({
      latest: latestLabel,
      replacedOn: replacedOnLabel,
      dataMissing: dataMissingLabel,
    }),
    [dataMissingLabel, latestLabel, replacedOnLabel],
  );
  const uploadList = usePurchaseTrendFileCheck(
    lockedFilters,
    monthLabels.short,
    uploadOptionLabels,
  );
  const { selectedUploadId, setSelectedUploadId, hasNoFile, reportEnabled } =
    uploadList;
  const filters = usePurchaseTrendFilters(lockedFilters, reportEnabled);
  const chart = usePurchaseTrendChartModel(
    uploadList.selectedYear,
    uploadList.resolvedUploadId,
    filters.selectedSuppliers,
    filters.selectedDivisions,
    monthLabels.short,
    monthLabels.full,
    reportEnabled,
  );
  const selectedSuppliers = filters.selectedSuppliers;
  const selectedDivisions = lockedFilters
    ? optionsFromLockedIds(
        lockedFilters.divisionIds,
        lockedLabelsFromNamed(lockedFilters.divisionIds, chart.scopedDivisions),
        unlabeled,
      )
    : filters.selectedDivisions;
  const title = buildPurchaseTrendTitle({
    year: uploadList.selectedYear,
    supplierLabels: selectedSuppliers.map((option) => option.label),
    divisionLabels: selectedDivisions.map((option) => option.label),
  });
  const upload = usePurchaseTrendUpload({
    selectedYear: uploadList.selectedYear,
    uploads: uploadList.uploads,
    resolvedUploadId: uploadList.resolvedUploadId,
    onSelectLatest: () => setSelectedUploadId(null),
    monthLabels: monthLabels.short,
    chartSourceMonths: chart.chartSourceMonths,
    uploadsInView: chart.uploadsInView,
  });
  const deleteUpload = useDeletePurchaseUpload();
  const canRemoveUploads = upload.canUpload && !filters.isShared;
  async function handleRemoveUpload(uploadId: string) {
    await deleteUpload.mutateAsync({ uploadId });
    if (
      selectedUploadId === uploadId ||
      uploadList.resolvedUploadId === uploadId
    ) {
      setSelectedUploadId(null);
    }
  }
  const loadInput = {
    hasNoFile,
    suppliersLoading: filters.suppliersLoading,
    divisionsLoading: filters.divisionsLoading,
    uploadsLoading: uploadList.uploadsLoading,
    trendLoading: chart.trendLoading,
    hasTrendData: chart.hasTrendData,
    hasTrendError: Boolean(chart.trendError),
  };
  const hasCompletedInitialLoad = useHasCompletedInitialLoad(
    purchaseTrendIsSettled(loadInput),
  );
  const isInitialLoading = purchaseTrendInitialLoading({
    hasCompletedInitialLoad,
    ...loadInput,
  });
  return {
    isShared: filters.isShared,
    selectedYear: uploadList.selectedYear,
    selectedUploadId: uploadList.resolvedUploadId,
    setSelectedUploadId,
    selectedSuppliers,
    setSelectedSuppliers: filters.setSelectedSuppliers,
    selectedDivisions,
    setSelectedDivisions: filters.setSelectedDivisions,
    uploadOptions: uploadList.uploadOptions,
    supplierOptions: filters.supplierOptions,
    divisionOptions: filters.divisionOptions,
    chartRows: chart.chartRows,
    cells: chart.cells,
    title,
    subtitle: chart.subtitle,
    reportedAlgorithm: chart.reportedAlgorithm,
    supplierDataThrough: chart.supplierDataThrough,
    forecastUnavailableReason: chart.forecastUnavailableReason,
    unmatchedPurchaseSuppliers: chart.unmatchedPurchaseSuppliers,
    purchaseSuppliersWithoutRebateAgreement:
      chart.purchaseSuppliersWithoutRebateAgreement,
    unmatchedDivisionNames: chart.unmatchedDivisionNames,
    unmatchedPurchaseDivisions: chart.unmatchedPurchaseDivisions,
    appliedPurchaseMappings: chart.appliedPurchaseMappings,
    chartStatus: chart.chartStatus,
    isInitialLoading,
    hasNoFile,
    uploadOpen: upload.uploadOpen,
    setUploadOpen: upload.setUploadOpen,
    setLastUpload: upload.handleImported,
    canUpload: upload.canUpload && !filters.isShared,
    canRemoveUploads,
    removeUploadLoading: deleteUpload.isPending,
    onRemoveUpload: handleRemoveUpload,
    lastUploadCard: upload.lastUploadCard,
  };
}
